import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { InviteModal } from '../invite/InviteModal';

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  collapsed?: boolean;
}

function NavItem({ to, icon, label, collapsed = true }: NavItemProps): JSX.Element {
  return (
    <NavLink
      to={to}
      className={({ isActive }) => {
        const base = [
          'flex items-center gap-3',
          'px-3 py-2.5',
          'rounded-comp',
          'transition-all duration-150',
          'text-text-secondary',
          'hover:bg-surface-base hover:text-text-primary',
        ].join(' ');

        const active = isActive
          ? 'bg-surface-elevated text-accent-brand font-medium shadow-sm'
          : '';

        return `${base} ${active}`;
      }}
      title={collapsed ? label : undefined}
    >
      <span className="w-5 h-5 flex-shrink-0">{icon}</span>
      {!collapsed && <span className="text-sm font-medium">{label}</span>}
    </NavLink>
  );
}

function InboxIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-full h-full"
    >
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  );
}

function ChannelsIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-full h-full"
    >
      <path d="M4 11a9 9 0 0 1 9 9" />
      <path d="M4 4a16 16 0 0 1 16 16" />
      <circle cx="5" cy="19" r="1" />
    </svg>
  );
}

function SettingsIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-full h-full"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function KnowledgeIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-full h-full"
    >
      <path d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
    </svg>
  );
}

function DashboardIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-full h-full"
    >
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </svg>
  );
}

function InviteIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-full h-full"
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <line x1="19" y1="8" x2="19" y2="14" />
      <line x1="22" y1="11" x2="16" y2="11" />
    </svg>
  );
}

function LogoutIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-full h-full"
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

interface SidebarProps {
  collapsed?: boolean;
  onLogout?: (() => void) | undefined;
}

export function Sidebar({ collapsed = true, onLogout }: SidebarProps): JSX.Element {
  const { user } = useAuth();
  const [showInvite, setShowInvite] = useState(false);
  const canInvite = user?.role === 'owner' || user?.role === 'admin';

  return (
    <>
      <aside
        className={[
          'flex flex-col',
          'bg-surface-subtle',
          'border-r border-border-subtle',
          'h-screen',
          collapsed ? 'w-16' : 'w-64',
          'transition-all duration-200',
        ].join(' ')}
      >
        {/* Логотип */}
        <div className="flex items-center justify-center h-16 border-b border-border-subtle">
          <div className="w-8 h-8 rounded-comp bg-accent-ai/10 border border-accent-ai/20 flex items-center justify-center shadow-sm">
            <span className="text-accent-ai font-bold text-lg">A</span>
          </div>
          {!collapsed && (
            <span className="ml-3 font-heading font-bold text-text-primary tracking-tight">
              Anima
            </span>
          )}
        </div>

        {/* Навигация */}
        <nav className="flex-1 px-2 py-4 space-y-1">
          <NavItem
            to="/inbox"
            icon={<InboxIcon />}
            label="Входящие"
            collapsed={collapsed}
          />
          <NavItem
            to="/channels"
            icon={<ChannelsIcon />}
            label="Каналы"
            collapsed={collapsed}
          />
          <NavItem
            to="/knowledge"
            icon={<KnowledgeIcon />}
            label="База знаний"
            collapsed={collapsed}
          />
          <NavItem
            to="/settings/auto-reply"
            icon={<SettingsIcon />}
            label="Настройки"
            collapsed={collapsed}
          />
          <NavItem
            to="/dashboard"
            icon={<DashboardIcon />}
            label="Дашборд"
            collapsed={collapsed}
          />
        </nav>

        {/* Footer */}
        <div className="px-2 py-4 border-t border-border-subtle space-y-1">
          {/* Invite button — owner/admin only */}
          {canInvite && (
            <button
              onClick={() => setShowInvite(true)}
              className={[
                'flex items-center gap-3',
                'w-full px-3 py-2.5',
                'rounded-comp',
                'text-text-secondary',
                'hover:bg-accent-brand/10 hover:text-accent-brand',
                'transition-colors duration-150',
              ].join(' ')}
              title={collapsed ? 'Пригласить' : undefined}
            >
              <span className="w-5 h-5 flex-shrink-0">
                <InviteIcon />
              </span>
              {!collapsed && <span className="text-sm font-medium">Пригласить</span>}
            </button>
          )}

          {/* Logout */}
          <button
            onClick={onLogout}
            className={[
              'flex items-center gap-3',
              'w-full px-3 py-2.5',
              'rounded-comp',
              'text-text-secondary',
              'hover:bg-accent-danger/10 hover:text-accent-danger',
              'transition-colors duration-150',
            ].join(' ')}
            title={collapsed ? 'Выйти' : undefined}
          >
            <span className="w-5 h-5 flex-shrink-0">
              <LogoutIcon />
            </span>
            {!collapsed && <span className="text-sm font-medium">Выйти</span>}
          </button>
        </div>
      </aside>

      {/* Invite Modal */}
      {showInvite && <InviteModal onClose={() => setShowInvite(false)} />}
    </>
  );
}
